import type { TicketBasketType } from "@shared/modules/payments/types";
import type { AppleWalletPass } from "@app-contexts/user/features/my-orders/data/types/user-ticket-item-types";

declare global {
  interface Window {
    ReactNativeWebView?: {
      postMessage(message: string): void;
    };
  }
}

export const WebviewActions = {
  RESTART_CART: "RESTART_CART",
  CART_EXPIRY: "CART_EXPIRY",
  LOGIN: "LOGIN",
  VIEW_ORDER: "VIEW_ORDER",
  RETURN_TO_EXPLORE: "RETURN_TO_EXPLORE",
  PURCHASE_COMPLETE: "PURCHASE_COMPLETE",
  OPEN_EVENT: "OPEN_EVENT",
  SEARCH_VENUE_EVENTS: "SEARCH_VENUE_EVENTS",
  OPEN_MEMBERSHIP_RENEWAL_CHECKOUT: "OPEN_MEMBERSHIP_RENEWAL_CHECKOUT",
  OPEN_MEMBERSHIP_RENEWAL_DETAIL: "OPEN_MEMBERSHIP_RENEWAL_DETAIL",
  PDF_DOWNLOAD: "PDF_DOWNLOAD",
  CART_UPDATE: "CART_UPDATE",
  OPEN_APPLE_WALLET: "OPEN_APPLE_WALLET",
  OPEN_GOOGLE_WALLET: "OPEN_GOOGLE_WALLET",
  OPEN_APPLE_WALLET_MULTI: "OPEN_APPLE_WALLET_MULTI",
  PROFILE_UPDATED: "PROFILE_UPDATED",
  CHECKOUT_STEP: "CHECKOUT_STEP",
} as const;

// Used to differentiate between messages from the webview and messages from other sources
// in the React Native application's message handling system while moving to new checkout.
export const WEBVIEW_SOURCE = "SP_WEBVIEW" as const;

export type WebviewAction =
  (typeof WebviewActions)[keyof typeof WebviewActions];

export type WebviewMessageMap = {
  // No payload
  RESTART_CART: undefined;
  CART_EXPIRY: undefined;
  LOGIN: undefined;
  RETURN_TO_EXPLORE: undefined;
  PURCHASE_COMPLETE: undefined;
  PROFILE_UPDATED: undefined;
  OPEN_MEMBERSHIP_RENEWAL_CHECKOUT: { venueId?: number };

  // With payload
  PDF_DOWNLOAD: { url: string };
  OPEN_APPLE_WALLET: { url: string };
  OPEN_GOOGLE_WALLET: { url: string };
  OPEN_MEMBERSHIP_RENEWAL_DETAIL: { membershipRoute: string; venueId?: number };
  CART_UPDATE: { cartData: TicketBasketType | null };
  OPEN_EVENT: {
    event: { id?: number; name: string; slug?: string } | undefined;
    venueId?: number;
  };
  SEARCH_VENUE_EVENTS: {
    venue: { id?: number; name: string; slug?: string } | undefined;
  };
  OPEN_APPLE_WALLET_MULTI: { passes: AppleWalletPass[] };
  VIEW_ORDER: { transactionId: string } | { guestCheckoutOrderUrl: string };
  CHECKOUT_STEP: { hasPrevStep: boolean };
};

export type WebviewMessage = {
  [K in WebviewAction]: WebviewMessageMap[K] extends undefined
    ? { action: K; source: typeof WEBVIEW_SOURCE }
    : {
        action: K;
        payload: WebviewMessageMap[K];
        source: typeof WEBVIEW_SOURCE;
      };
}[WebviewAction];

type PostMessageArgs = {
  [K in WebviewAction]: WebviewMessageMap[K] extends undefined
    ? { action: K; payload?: undefined }
    : { action: K; payload: WebviewMessageMap[K] };
}[WebviewAction];

/**
 * Posts a message from web context to a React Native WebView.
 * This function uses the ReactNativeWebView.postMessage bridge to communicate
 * between web content and the React Native WebView component.
 *
 * @param action - The specific action to be performed
 * @param payload - Optional data associated with the action
 *
 * @example
 * postMessageToReactNativeWebView({ action: "RESTART_CART" });
 * postMessageToReactNativeWebView({ action: "PDF_DOWNLOAD", payload: { url: "https://..." } });
 */
export function postMessageToReactNativeWebView({
  action,
  payload,
}: PostMessageArgs): void {
  if (typeof window !== "undefined" && window.ReactNativeWebView) {
    const message = {
      action,
      source: WEBVIEW_SOURCE,
      ...(payload !== undefined && { payload }),
    };
    window.ReactNativeWebView.postMessage(JSON.stringify(message));
  }
}
